import {create} from 'zustand';
import type {ClassifyPrediction, Transaction} from '../types';
import {classifyByDescription, classifyImage} from '../api/cards';
import {preprocessImage} from '../utils/image';

type SearchMode = 'idle' | 'text' | 'image';

interface SearchState {
  // 搜索模式
  mode: SearchMode;
  language: string;
  series: string;
  generation: number;
  setFilters: (language: string, series: string) => void;

  // 文字搜索状态
  searchQuery: string;
  submittedQuery: string;
  submittedFilters: { language: string; series: string };
  reachedEnd: boolean;
  transactions: Transaction[];
  totalCount: number;
  currentPage: number;
  pageSize: number;

  // 图片识别状态
  candidates: ClassifyPrediction[];
  selectedCandidate: ClassifyPrediction | null;
  imagePreviewUrl: string | null;

  // 通用状态
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  paginationError: string | null;

  // Actions
  setSearchQuery: (query: string) => void;
  setImagePreview: (file: File | null) => void;
  searchByText: (query: string, page?: number) => Promise<void>;
  searchByImage: (file: File) => Promise<void>;
  loadMoreCandidates: () => Promise<void>;
  selectCandidate: (candidate: ClassifyPrediction) => void;
  clearSearch: () => void;
  setPage: (page: number) => void;
  hasMoreCandidates: () => boolean;
}

export const useSearchStore = create<SearchState>((set, get) => ({
  mode: 'idle',
  language: '', series: '', generation: 0,
  setFilters: (language, series) => set({language,series,currentPage:1}),
  searchQuery: '',
  submittedQuery: '',
  submittedFilters: { language: '', series: '' },
  reachedEnd: false,
  transactions: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,
  candidates: [],
  selectedCandidate: null,
  imagePreviewUrl: null,
  isLoading: false,
  isLoadingMore: false,
  error: null,
  paginationError: null,

  setSearchQuery: (query) => set({ searchQuery: query }),
  setImagePreview: (file) => {
    const { imagePreviewUrl } = get();
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    const nextUrl = file ? URL.createObjectURL(file) : null;
    set({ imagePreviewUrl: nextUrl });
  },

  searchByText: async (query, page = 1) => {
    if (!query.trim() && !get().language && !get().series) return;
    const generation = get().generation + 1;
    const submittedFilters = { language: get().language, series: get().series };
    set({ generation, isLoadingMore: false, paginationError: null, reachedEnd: false,
      submittedQuery: query, submittedFilters });

    get().setImagePreview(null);
    const { pageSize } = get();
    set({ isLoading: true, error: null, mode: 'text', searchQuery: query });

    try {
      // 调用新的根据描述分类接口，返回候选卡片列表（支持分页）
      const response = await classifyByDescription(query, page, pageSize, submittedFilters);
      if(get().generation !== generation) return;
      if (response.code === 0 && response.data?.predictions) {
        set({
          candidates: response.data.predictions,
          totalCount: response.data.total ?? response.data.predictions.length,
          currentPage: response.data.currentPage || page,
          reachedEnd: response.data.predictions.length === 0
            || (response.data.totalPages !== undefined && (response.data.currentPage || page) >= response.data.totalPages),
          isLoading: false,
        });
      } else {
        set({
          error: response.message || 'Search failed',
          isLoading: false,
        });
      }
    } catch (error: any) {
      if(get().generation !== generation) return;
      set({
        error: error.response?.data?.message || 'Search failed. Please try again.',
        isLoading: false,
      });
    }
  },

  searchByImage: async (file) => {
    const generation=get().generation+1;
    set({generation,language:'',series:'',isLoadingMore:false,paginationError:null,reachedEnd:false});
    get().setImagePreview(file);
    // 清空搜索框文字，避免用户误以为结果来自关键字搜索
    set({ isLoading: true, error: null, mode: 'image', candidates: [], searchQuery: '' });

    try {
      const processedFile = await preprocessImage(file);
      const response = await classifyImage(processedFile);
      if(get().generation !== generation) return;
      if (response.code === 0 && response.data?.predictions) {
        set({
          candidates: response.data.predictions,
          isLoading: false,
        });
      } else {
        set({
          error: response.message || 'Recognition failed',
          isLoading: false,
        });
      }
    } catch (error: any) {
      if(get().generation !== generation) return;
      set({
        error: error.response?.data?.message || 'Image recognition failed. Please try again.',
        isLoading: false,
      });
    }
  },

  selectCandidate: (candidate) => {
    set({ selectedCandidate: candidate });
  },

  clearSearch: () => {
    set({generation:get().generation+1,language:'',series:'',isLoading:false});
    get().setImagePreview(null);
    set({
      mode: 'idle',
      searchQuery: '',
      transactions: [],
      totalCount: 0,
      currentPage: 1,
      candidates: [],
      selectedCandidate: null,
      imagePreviewUrl: null,
      isLoadingMore: false,
      error: null,
      paginationError: null,
      submittedQuery: '',
      submittedFilters: { language: '', series: '' },
      reachedEnd: false,
    });
  },

  setPage: (page) => {
    const { searchQuery, mode } = get();
    if (mode === 'text' && (searchQuery || get().language || get().series)) {
      get().searchByText(searchQuery, page);
    }
  },

  hasMoreCandidates: () => {
    const { candidates, totalCount, mode, reachedEnd } = get();
    return mode === 'text' && !reachedEnd && candidates.length < totalCount;
  },

  loadMoreCandidates: async () => {
    const { submittedQuery, submittedFilters, currentPage, pageSize, isLoading, isLoadingMore, candidates } = get();
    
    // 如果正在加载或没有更多数据，直接返回
    if (isLoading || isLoadingMore || !get().hasMoreCandidates()) {
      return;
    }

    const generation=get().generation;
    const nextPage = currentPage + 1;
    set({ isLoadingMore: true, paginationError: null });

    try {
      const response = await classifyByDescription(submittedQuery, nextPage, pageSize, submittedFilters);
      if(get().generation !== generation) return;
      if (response.code === 0 && response.data?.predictions) {
        const ids = new Set(candidates.map((candidate) => candidate.id_label));
        const additions = response.data.predictions.filter((candidate) => {
          if (ids.has(candidate.id_label)) return false;
          ids.add(candidate.id_label);
          return true;
        });
        const page = response.data.currentPage || nextPage;
        set({
          candidates: page >= nextPage ? [...candidates, ...additions] : candidates,
          currentPage: page,
          totalCount: response.data.total ?? get().totalCount,
          reachedEnd: additions.length === 0 || page < nextPage
            || (response.data.totalPages !== undefined && page >= response.data.totalPages),
          isLoadingMore: false,
        });
      } else {
        set({ isLoadingMore: false, paginationError: response.message || 'Could not load more identities.' });
      }
    } catch (error: any) {
      if(get().generation !== generation) return;
      set({ isLoadingMore: false,
        paginationError: error.response?.data?.message || 'Could not load more identities. Please retry.' });
    }
  },
}));
